import { normalizeInspectionRecommendations } from "./inspectionRecommendation";

const uniqueId = () =>
  `diagnostico-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const copyTargets = (targets, type) =>
  (targets || []).map((item) => ({
    ...item,
    id:
      item.id ||
      `mip-${type}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  }));

export const isAgendaMetadata = (value) => {
  if (!value || typeof value !== "string") return false;
  try {
    const parsed = JSON.parse(value);
    return parsed && parsed.meta === true;
  } catch (_) {
    return false;
  }
};

export const inspectionTechnicalNotes = (value) =>
  isAgendaMetadata(value) ? "" : value || "";

export const emptySampling = () => ({
  method: "Pano de batida e inspeção visual",
  samplePoints: null,
  assessedArea: null,
  distribution: "Zigue-zague",
  notes: "",
});

export const createInspectionDiagnosis = (source = {}) => ({
  id: source.id || uniqueId(),
  talhaoId: source.talhaoId || "",
  nomeTalhao: source.nomeTalhao || source.talhaoNome || "",
  talhaoMode: source.talhaoMode || "registered",
  culturaSelecionada: source.culturaSelecionada || source.culturaId || "",
  cultureName: source.cultureName || source.culturaNome || "",
  cultureMode: source.cultureMode || "registered",
  variedade: source.variedade || "",
  estadioFenologico: source.estadioFenologico || "",
  umidadeSolo: source.umidadeSolo || "",
  mipSampling: { ...emptySampling(), ...(source.mipSampling || {}) },
  alvosPragas: copyTargets(source.alvosPragas, "praga"),
  alvosDoencas: copyTargets(source.alvosDoencas, "doenca"),
  alvosDaninhas: copyTargets(source.alvosDaninhas, "daninha"),
  dadosAdicionais: inspectionTechnicalNotes(source.dadosAdicionais),
  recomendacaoManejo: source.recomendacaoManejo || "",
  recommendedProducts: normalizeInspectionRecommendations(
    source.recommendedProducts || source.produtosRecomendados
  ),
  recipeId: source.recipeId || "",
});

export const diagnosisTargetCount = (diagnosis) =>
  ["alvosPragas", "alvosDoencas", "alvosDaninhas"].reduce(
    (total, field) => total + (diagnosis?.[field]?.length || 0),
    0
  );

export const normalizeInspectionDiagnoses = (inspection) => {
  if (Array.isArray(inspection.diagnosticos) && inspection.diagnosticos.length) {
    return inspection.diagnosticos.map(createInspectionDiagnosis);
  }

  return [
    createInspectionDiagnosis({
      nomeTalhao: inspection.nomeTalhao,
      culturaSelecionada: inspection.culturaSelecionada,
      cultureName: inspection.cultureName,
      variedade: inspection.variedade,
      estadioFenologico: inspection.estadioFenologico,
      umidadeSolo: inspection.umidadeSolo,
      mipSampling: inspection.mipSampling,
      alvosPragas: inspection.alvosPragas,
      alvosDoencas: inspection.alvosDoencas,
      alvosDaninhas: inspection.alvosDaninhas,
      dadosAdicionais: inspection.dadosAdicionais,
      recomendacaoManejo: inspection.recomendacaoManejo,
      recommendedProducts:
        inspection.recommendedProducts || inspection.produtosRecomendados,
      recipeId: inspection.recipeId,
    }),
  ];
};

const withoutIdentity = (item) => {
  const copy = { ...item };
  delete copy.id;
  return copy;
};

export const cloneInspectionAsDraft = (inspection) => {
  const diagnosisIdMap = new Map();
  const diagnoses = normalizeInspectionDiagnoses(inspection).map((source) => {
    const targetIdMap = new Map();
    const copyTargetCollection = (targets, type) =>
      (targets || []).map((target) => {
        const clone = copyTargets([withoutIdentity(target)], type)[0];
        targetIdMap.set(String(target.id), clone.id);
        return clone;
      });

    const diagnosis = createInspectionDiagnosis({
      ...source,
      id: null,
      recipeId: "",
      alvosPragas: copyTargetCollection(source.alvosPragas, "praga"),
      alvosDoencas: copyTargetCollection(source.alvosDoencas, "doenca"),
      alvosDaninhas: copyTargetCollection(source.alvosDaninhas, "daninha"),
      recommendedProducts: [],
    });
    diagnosisIdMap.set(String(source.id), diagnosis.id);
    diagnosis.recommendedProducts = normalizeInspectionRecommendations(
      (source.recommendedProducts || []).map((product) => ({
        ...product,
        id: null,
        diagnosisId: diagnosis.id,
        targetId: targetIdMap.get(String(product.targetId)) || product.targetId,
      }))
    );
    return diagnosis;
  });

  const clone = {
    ...inspection,
    id: "",
    numero: undefined,
    commitmentId: null,
    status: "Aberto",
    recipeId: "",
    resolved: null,
    followUpRequired: false,
    followUpDate: null,
    diagnosticos: diagnoses,
    pendingActions: (inspection.pendingActions || []).map((action) => ({
      ...action,
      id: undefined,
      completedAt: null,
      status: "Pendente",
      statusHistory: [{ status: "Pendente", date: new Date().toISOString() }],
    })),
  };

  const attachments = inspection.fotos || inspection.attachments || [];
  clone.fotos = attachments.map((attachment) => {
    if (typeof attachment === "string") return attachment;
    const sourceDiagnosisId =
      attachment.diagnosisId || attachment.diagnosticoId || null;
    return {
      ...attachment,
      id: undefined,
      diagnosisId: sourceDiagnosisId
        ? diagnosisIdMap.get(String(sourceDiagnosisId)) || null
        : null,
      diagnosticoId: undefined,
    };
  });
  delete clone.attachments;

  return clone;
};
